import { FC, useEffect, useRef, useState } from 'react';

import { Box, Divider, Stack, Typography } from '@mui/material';

import { CfsApi } from '@/api';
import { IAdditionalInfo } from '@/models';

import { AdditionalInfoInput } from './AdditionalInfoInput';
import { AdditionalInfoItem } from './AdditionalInfoItem';
import { AdditionalInfoList } from './styles';

interface IAdditionalInfoProps {
  cfsId?: string;
  additionalInfoList?: IAdditionalInfo[];
  onSubmit: (
    value: string,
    isEditing?: boolean,
    editingItem?: IAdditionalInfo | null,
  ) => void;
  classes?: { additionalInfoList: string };
  setNewAdditionalInfo: (data: IAdditionalInfo[]) => void;
  isFirstResponderView?: boolean;
  disableSendMessage?: boolean;
  isFirstResponderModal?: boolean;
  maxChars?: number;
  mirrorOffsetX?: number;
  isMaxHeight?: boolean;
  readOnly?: boolean;
}

export const AdditionalInfoForm: FC<IAdditionalInfoProps> = ({
  cfsId,
  additionalInfoList = [],
  onSubmit,
  classes,
  setNewAdditionalInfo,
  isFirstResponderView,
  disableSendMessage,
  isFirstResponderModal,
  maxChars,
  mirrorOffsetX = 0,
  isMaxHeight = false,
  readOnly = false,
}) => {
  const infoListBottomRef = useRef<HTMLDivElement>(null);
  const [editingItem, setEditingItem] = useState<IAdditionalInfo | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  // Display the last message into the view port as default
  useEffect(() => {
    if (infoListBottomRef.current) {
      const observer = new ResizeObserver(() => {
        infoListBottomRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      });

      observer.observe(infoListBottomRef.current);
      return () => observer.disconnect();
    }
  }, [additionalInfoList.length]);

  // Change the signature and add the guard inside the callback
  const updateAdditionalInfo = (deletedItem?: IAdditionalInfo) => {
    CfsApi.update(cfsId as string, { additionalInfo: additionalInfoList }).then(
      (res) => {
        if (res.status === 200) {
          setNewAdditionalInfo(res?.data?.additionalInfo);

          // If the item currently being edited was deleted, clear editing state
          if (deletedItem && editingItem?._id === deletedItem._id) {
            setEditingItem(null);
            setIsEditing(false);
          }
        }
      },
    );
  };

  const handleEditAdditionalInfo = (additionalInfo: IAdditionalInfo) => {
    setEditingItem(additionalInfo);
    setIsEditing(true);
  };

  const getLegacyAdditionalInfoMaxHeight = () => {
    if (isFirstResponderView && isFirstResponderModal) {
      return { maxHeight: '220px' };
    }

    if (isFirstResponderView) {
      return { maxHeight: 'calc(100vh - 194px - 375px)' };
    }

    return { maxHeight: 'calc(100vh - 87px - 455px - 46px - 60px - 155px)' };
  };

  const additionalInfoListSx = isMaxHeight
    ? { minHeight: 0, maxHeight: '100%', flex: 1 }
    : { minHeight: 'auto', ...getLegacyAdditionalInfoMaxHeight() };

  useEffect(() => {
    if (!editingItem) {
      setIsEditing(false);
    }
  }, [editingItem]);

  return (
    <Stack
      rowGap={2}
      flex={1}
      minHeight={0}
      height={isMaxHeight ? '100%' : 'auto'}
      overflow={isMaxHeight ? 'hidden' : 'auto'}
    >
      <AdditionalInfoList
        className={classes?.additionalInfoList}
        sx={additionalInfoListSx}
      >
        {additionalInfoList.length ? (
          <Box className="content">
            {additionalInfoList
              .filter((item) => !item.isDeleted)
              .map((item, index) => (
                <AdditionalInfoItem
                  cfsId={cfsId}
                  key={`${item.createdBy?._id}-${index}`}
                  additionalInfo={item}
                  updateAdditionalInfos={(deletedItem) =>
                    updateAdditionalInfo(deletedItem)
                  }
                  onEdit={handleEditAdditionalInfo}
                  hideActions={readOnly}
                />
              ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography variant="body2">No message to display</Typography>
          </Box>
        )}
        {additionalInfoList.length > 0 && <Box ref={infoListBottomRef} />}
      </AdditionalInfoList>

      <Divider />
      <Box sx={{ flex: 'none' }}>
        {!disableSendMessage && !readOnly && (
          <AdditionalInfoInput
            placeholder="Message or use * for timeline integration"
            onSubmit={(value) => {
              onSubmit(value, isEditing, editingItem);
              setIsEditing(false);
              setEditingItem(null);
            }}
            initialValue={editingItem?.description}
            isEditing={isEditing}
            resetEdit={() => setIsEditing(false)}
            maxChars={maxChars}
            mirrorOffsetX={mirrorOffsetX}
          />
        )}
      </Box>
    </Stack>
  );
};
